<template>

    <div class="space-y-6">

        <div class="space-y-1">
            <p class="text-sm">
                Partnero API is built with RESTful principles and provides the most flexible and robust option to send customer details to Partnero via secure API calls. For more details, you can read the <a href="https://developers.partnero.com/reference/general.html" target="_blank" class="underline hover:no-underline">reference documentation</a>.
            </p>
        </div>

        <div class="space-y-2">
            <h3 class="text-md font-semibold">
                1. Create API key
            </h3>
            <div class="space-y-1">
                <p class="text-sm">
                    You can create API key <a href="../integrations" target="_blank" class="underline hover:no-underline">here</a>.
                </p>
            </div>
        </div>

        <!-- Affiliate -->
        <div class="space-y-4" v-if="programType === 'affiliate'">
            <div class="space-y-2">
                <h3 class="text-md font-semibold">
                    2. Send sign-up data
                </h3>
                <div class="space-y-1">
                    <p class="text-sm">
                        In this step, you’ll need to register referred customers in your Partnero account.
                    </p>
                    <p class="text-sm">
                        To do this, send the sign-up data to the Partnero <a href="https://developers.partnero.com/reference/customers.html#create-a-customer" target="_blank" class="underline hover:no-underline">Customer Create</a> API endpoint.
                    </p>
                    <p class="text-sm">
                        The payload should include two objects: <badge type="neutral" title="partner"></badge> and <badge type="neutral" title="customer"></badge>.
                        You must populate the <badge type="neutral" title="partner.key"></badge> field with the <badge type="neutral" title="UNIQUE_PARTNER_KEY"></badge>, which is stored in the <badge type="neutral" title="partnero_partner"></badge> cookie. This cookie is automatically created by PartneroJS snippet.                    </p>
                </div>
            </div>

            <div>
                <textarea rows="1" class="bg-gray-50 block w-full text-sm rounded border-gray-200 focus:ring-0 focus:border-gray-400 mb-1 scroll-h font-monospace text-xs">POST https://api.partnero.com/v1/customers</textarea>
            </div>
            <div class="text-sm font-semibold">cURL request example </div>
            <div class="relative">
                <code-snippet :code="customerExample" language="curl" />
            </div>
        </div>

        <!-- Refer-a-friend -->
        <div class="space-y-4" v-if="programType === 'refer_a_friend'">
            <div class="space-y-2">
                <h3 class="text-md font-semibold">
                    2. Send sign-up data
                </h3>
                <div class="space-y-1">
                    <p class="text-sm">In Refer-A-Friend program each customer of your application can be both referring customer and referred customer.</p>
                    <p class="text-sm">Every time your application gets a new signup you need to create a customer profile at Partnero.</p>
                    <p class="text-sm">
                        To do this, send the sign-up data to the Partnero <a href="https://developers.partnero.com/reference/referral-customers.html#create-a-customer" target="_blank" class="underline hover:no-underline">Customer Create</a> API endpoint.
                    </p>
                </div>
            </div>

            <div>
                <textarea rows="1" class="bg-gray-50 block w-full text-sm rounded border-gray-200 focus:ring-0 focus:border-gray-400 mb-1 scroll-h font-monospace text-xs">POST https://api.partnero.com/v1/customers</textarea>
            </div>
            <div class="text-sm font-semibold">cURL request example to create a customer</div>
            <div class="relative">
                <code-snippet :code="curlReferralExample" language="curl" />
            </div>

            <div class="text-sm font-semibold">cURL request example to create a referred customer</div>
            <div class="relative">
                <code-snippet :code="curlReferralExampleReferred" language="curl" />
            </div>

            <alert type="warning">
                This type of customer is created when you can find <b>partnero_referral</b> cookie, and the customer is recognized as referred customer.
            </alert>

            <div class="text-sm space-y-1 mb-4">
                <p>
                    Please note that while creating a customers you have to replace <b>key</b>, <b>name</b> and <b>email</b> with a real data from your platform.
                </p>
                <p>
                    Read full API documentation <a class="underline hover:no-underline" href="https://developers.partnero.com/reference/referral-customers.html#create-a-customer" target="_blank">here</a> or you can check Refer-a-Friend integration
                    <a class="underline hover:no-underline" href="https://developers.partnero.com/guide/referral.html#creating-customers" target="_blank">guide</a>.
                </p>
            </div>
        </div>

        <div class="space-y-4">
            <div class="space-y-2">
                <h3 class="text-md font-semibold">
                    3. Send sales data
                </h3>
                <div class="space-y-1">
                    <p class="text-sm">
                        The last part of the integration is sending data about sales. When any customer makes a purchase, it is recommended to call <a href="https://developers.partnero.com/reference/transactions.html#create-a-transaction" target="_blank" class="underline hover:no-underline">Transaction create</a>.
                    </p>
                    <p class="text-sm">
                        When making this call, we recommend including the most important data about the purchase. Also, feel free to send data about all sales; Partnero will ignore requests if there is no existing <badge type="neutral" title="customer.key"></badge>.
                    </p>
                </div>
            </div>
            <div class="relative">
                <textarea rows="1" class="bg-gray-50 block w-full text-sm rounded border-gray-200 focus:ring-0 focus:border-gray-400 mb-1 scroll-h font-monospace text-xs">POST https://api.partnero.com/v1/transactions</textarea>
            </div>
            <div class="text-sm font-semibold">cURL request example </div>
            <div class="relative">
                <code-snippet :code="transactionExample" language="curl" />
            </div>

        </div>

    </div>

</template>

<script setup>

const props = defineProps({
    programId: {
        type: Number,
        required: true
    },
    config: {
        type: Object,
        default: null
    },
    programType: {
        type: String
    }
});

const transactionExample = `curl --location --request POST 'https://api.partnero.com/v1/customers' \
--header 'Accept: application/json' \
--header 'Authorization: Bearer YOUR_API_TOKEN' \
--header 'Content-Type: application/json' \
--data-raw '{
"customer": {
    "key": "CUSTOMER_KEY"
},
"key": "transaction_123",
    "amount": 99.99,
    "product_id": "prod_123",
    "product_type": "monthly",
    "action": "sale"
}'`

const customerExample = `curl --location --request POST 'https://api.partnero.com/v1/customers' \
--header 'Accept: application/json' \
--header 'Authorization: Bearer YOUR_API_TOKEN' \
--header 'Content-Type: application/json' \
--data-raw '{
"partner": {
"key": "UNIQUE_PARTNER_KEY"
},
"key": "CUSTOMER_KEY",
"email": "customer@partnero.com",
"name": "Partnero",
"surname": "Customer"
}'`


const curlReferralExample = `curl --location --request POST 'https://api.partnero.com/v1/customers' \
--header 'Accept: application/json' \
--header 'Authorization: Bearer YOUR_API_TOKEN' \
--header 'Content-Type: application/json' \
--data-raw '{
  "id": "referring_id",
  "key": "referring_key",
  "name": "Customer",
  "email": "customer@partnero.com",
  "metadata": {
    "date_of_birth": "1993-05-19"
  }
}'`

const curlReferralExampleReferred = `curl --location --request POST 'https://api.partnero.com/v1/customers' \
--header 'Accept: application/json' \
--header 'Authorization: Bearer YOUR_API_TOKEN' \
--header 'Content-Type: application/json' \
--data-raw '{
  "id": "referred_id",
  "key": "referred_key",
  "name": "Referred customer",
  "email": "referred-customer@partnero.com",
  "referring_customer": {
    "id": "referring_id",
    "key": "referring_key"
  },
  "metadata": {
    "date_of_birth": "1993-05-19"
  }
}'`

</script>

<style scoped>

</style>
